import { useEffect, useMemo } from 'react';
import { Session } from '@beyond-js/ui/dom';
import { useSession } from '@beyond-js/ui/react';
import { type Accounts, type Identity, Renewal } from '@accounts/domain';
import type { Language } from '@accounts/locale';

/** What the keeper made: the page's session */
export interface Kept {
	session: Session | null;
}

/**
 * The page's session for one signed-in person (the family's session rule, D63): rendered by `Presence`
 * with the person and the language as its key, so someone else signing in here (the local switch, a
 * sign-in after `/leave`) gets a session of their own and never inherits the previous one.
 *
 * A session that ends while the person works is answered by `Session`, never by a screen: Accounts has
 * no silent renewal (`Renewal`), so it asks at once with one dialog, which cannot be dismissed (D63 as
 * amended on 2026-10-07): there is no state in which this interface is open without a session, and a
 * request made while it asks waits behind it. "Use another account" is "Sign out of Beyond" (`/leave`); a suspended account is offered its own account page; someone else signing in
 * in the window starts this interface again at its entry. Renewed, `onrenewed` reads the view again.
 */
export function Keeper({ identity, accounts, language, onmade, onrenewed }: { identity: Identity; accounts: Accounts; language: Language; onmade: (kept: Kept) => void; onrenewed: () => void }) {
	const renewal = useMemo(() => new Renewal(accounts, globalThis.location, identity.account.email), [accounts, identity.account.email]);
	const { session } = useSession({
		product: Renewal.PRODUCT,
		person: { id: identity.account.id, name: identity.account.name ?? null },
		expires: identity.session.expires,
		read: () => renewal.read(),
		start: mode => renewal.start(mode),
		other: () => globalThis.location.assign('/leave'),
		accounts: renewal.account,
		onrenewed,
		// A full load: whatever was held for the previous person goes with the page
		onchanged: () => globalThis.location.assign('/'),
		labels: Session.labels[language]
	});

	useEffect(() => onmade({ session }), [onmade, session]);
	useEffect(() => () => onmade({ session: null }), [onmade]);
	return null;
}
